import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { ministryApi, MAX_POSTER_FILE_SIZE } from '../utils/api'
import { getUpcomingSundayOptions, getRecentSundayDateStrs, formatDateDisplay } from '../utils/sundayDisplay'
import { t } from '../utils/translations'
import AppBranding from '../components/AppBranding'

const CHURCHES = ['KTC', 'SSC', 'WIC', 'STC']
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff']

/** Ordered list of unique keys = current display order. */
function orderKeys(images) {
  return images.map(img => img.key)
}

export default function MinistryPage() {
  const { user } = useAuth()
  const [lang] = useState('zh')
  const zh = lang === 'zh'

  const userChurches = useMemo(() => {
    if (user?.role === 'sysadmin' || user?.role === 'admin') {
      if (user?.role === 'admin' && (!user?.churches || user.churches.length === 0)) return CHURCHES
    }
    return user?.churches || (user?.church ? [user.church] : [])
  }, [user])

  const sundayOptions = useMemo(() => {
    const upcoming = getUpcomingSundayOptions()
    return [upcoming.nextSunday, upcoming.nextNextSunday, upcoming.nextNextNextSunday]
  }, [])
  const recentSundays = useMemo(() => getRecentSundayDateStrs(4), [])

  const [church, setChurch] = useState(userChurches[0] || 'KTC')
  const [date, setDate] = useState(sundayOptions[0] || '')
  const [expectedCount, setExpectedCount] = useState(1)

  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploadingCount, setUploadingCount] = useState(0)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [dirty, setDirty] = useState(false) // order changed but not applied
  const fileInputRef = useRef(null)
  const pickIndexRef = useRef(0)

  const listImages = useCallback(async (silent = false) => {
    if (!church || !date) return
    if (!silent) setLoading(true)
    try {
      const { data: { data } } = await ministryApi.list(church, date, true)
      setImages(data.images || [])
      setDirty(false)
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.message || err.message || (zh ? '載入失敗' : 'Failed to load'))
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [church, date, zh])

  // Reload whenever church or date changes
  useEffect(() => {
    setImages([])
    setDirty(false)
    setError('')
    setSuccess('')
    listImages()
  }, [church, date])

  const canEdit = user?.role !== 'viewer'

  const filenameFor = (seq) => `${date}${church}家事報告${String(seq).padStart(3, '0')}.jpg`

  /** Upload one file; confirm returns its final seq (server assigns next free NNN). */
  const uploadOne = async (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_IMAGE_EXTENSIONS.includes(ext)) {
      throw new Error(zh ? `不支援的檔案類型：${file.name}` : `Unsupported file type: ${file.name}`)
    }
    if (file.size > MAX_POSTER_FILE_SIZE) {
      throw new Error(zh ? `檔案太大（上限 10MB）：${file.name}` : `File too large (max 10MB): ${file.name}`)
    }
    const confirmed = await ministryApi.uploadImage(file, { church, eventDate: date })
    return confirmed
  }

  /** Multi-file picker: uploads every chosen file, sequentially to keep server numbering stable. */
  const handleFilesChosen = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) return
    setError('')
    setSuccess('')
    setUploadingCount(files.length)
    const failures = []
    for (const file of files) {
      try {
        await uploadOne(file)
      } catch (err) {
        console.error('Ministry upload failed:', err)
        failures.push(`${file.name}: ${err.response?.data?.message || err.message}`)
      }
      setUploadingCount(c => Math.max(0, c - 1))
    }
    await listImages(true)
    if (failures.length > 0) {
      setError(failures.join('；'))
    } else {
      setSuccess(zh ? `已上傳 ${files.length} 張圖片` : `Uploaded ${files.length} image(s)`)
    }
  }

  /** "+ add one more" slots — number inputs only drive hint text; real slots come from server. */
  const addSlotViaPicker = () => {
    pickIndexRef.current = 0
    fileInputRef.current?.click()
  }

  const handleDelete = async (img) => {
    if (!window.confirm(zh ? `確定刪除 ${img.filename}？` : `Delete ${img.filename}?`)) return
    setError('')
    setSuccess('')
    try {
      await ministryApi.deleteImage(img.key)
      setSuccess(zh ? `已刪除 ${img.filename}` : `Deleted ${img.filename}`)
      await listImages(true)
    } catch (err) {
      console.error('Ministry delete failed:', err)
      setError(err.response?.data?.message || err.message || (zh ? '刪除失敗' : 'Delete failed'))
    }
  }

  const moveImage = (index, dir) => {
    setImages(prev => {
      const next = [...prev]
      const target = index + dir
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
    setDirty(true)
  }

  /** Apply current display order: server renumbers S3 keys to 001..NNN. */
  const handleApplyOrder = async () => {
    setError('')
    setSuccess('')
    setApplying(true)
    try {
      await ministryApi.applyOrder(church, date, orderKeys(images))
      setSuccess(zh ? '已更新編號次序' : 'Renumbered')
      await listImages(true)
    } catch (err) {
      console.error('Ministry apply order failed:', err)
      setError(err.response?.data?.message || err.message || (zh ? '更新次序失敗' : 'Reorder failed'))
    } finally {
      setApplying(false)
    }
  }

  const dateChoices = Array.from(new Set([...sundayOptions, ...recentSundays]))

  return (
    <div className="ministry-page">
      <div className="page-top">
        <div>
          <h2>{zh ? '家事報告' : 'Ministry Report'}</h2>
          <p className="sub">{zh
            ? '上傳各堂家事報告圖片，會自動置中裁切為 16:9、1280×720 JPEG'
            : 'Upload weekly ministry report images — auto center-cropped to 16:9, 1280×720 JPEG'}</p>
        </div>
        <AppBranding lang={lang} showControls={false} />
      </div>

      {error && <div className="message message-error">{error}</div>}
      {success && <div className="message message-success">{success}</div>}

      <div className="ministry-controls card">
        <div className="form-row">
          <div className="form-group">
            <label>{t('church', lang)} *</label>
            <select value={church} onChange={e => setChurch(e.target.value)}>
              {userChurches.map(c => (
                <option key={c} value={c}>{t(c, lang)}</option>
              ))}
            </select>
          </div>

          <div className="form-group form-group-date">
            <label>{t('eventDate', lang)} *</label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="YYYYMMDD"
              maxLength={8}
              value={date}
              onChange={e => setDate(e.target.value.replace(/\D/g, '').slice(0, 8))}
            />
            <div className="quick-dates">
              <span className="quick-label">{zh ? '點擊填入：' : 'Quick fill:'}</span>
              {dateChoices.map(d => (
                <button key={d} type="button" className="btn-quick-date" onClick={() => setDate(d)}>
                  {formatDateDisplay(d, lang)}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group form-group-count">
            <label>{zh ? '預計圖片數量' : 'Expected pictures'}</label>
            <div className="count-stepper">
              <button type="button" onClick={() => setExpectedCount(c => Math.max(1, c - 1))}>−</button>
              <span>{expectedCount}</span>
              <button type="button" onClick={() => setExpectedCount(c => Math.min(20, c + 1))}>+</button>
            </div>
            <button type="button" className="btn btn-primary btn-upload" onClick={addSlotViaPicker} disabled={uploadingCount > 0}>
              {uploadingCount > 0
                ? (zh ? `上傳中…（剩 ${uploadingCount}）` : `Uploading… (${uploadingCount} left)`)
                : (zh ? '選擇圖片上傳' : 'Choose pictures')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_IMAGE_EXTENSIONS.map(ext => `.${ext}`).join(',')}
              multiple
              onChange={handleFilesChosen}
              style={{ display: 'none' }}
            />
          </div>
        </div>
        <p className="hint">
          {zh
            ? `檔名會自動命名為 ${filenameFor(1)} 起（001、002…），建議一次選齊多張，次序可之後調整。`
            : `Files are auto-named from ${filenameFor(1)} (001, 002…). Pick several at once; reorder later.`}
        </p>
      </div>

      <div className="ministry-images card">
        <div className="images-header">
          <h3>{zh ? `已上傳圖片（${images.length}）` : `Uploaded images (${images.length})`}</h3>
          <div className="images-actions">
            {dirty && (
              <span className="dirty-hint">{zh ? '次序已更改' : 'Order changed'}</span>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleApplyOrder}
              disabled={!dirty || applying || images.length === 0}
            >
              {applying ? (zh ? '更新中…' : 'Applying…') : (zh ? 'Apply 更新編號' : 'Apply renumber')}
            </button>
          </div>
        </div>

        {loading ? (
          <p className="hint">{zh ? '載入中…' : 'Loading…'}</p>
        ) : images.length === 0 ? (
          <p className="hint">{zh ? '此日期尚未上傳任何圖片。' : 'No images uploaded for this date yet.'}</p>
        ) : (
          <div className="image-grid">
            {images.map((img, index) => (
              <div className="image-card" key={img.key}>
                <div className="thumb-wrap">
                  {img.downloadUrl
                    ? <img src={img.downloadUrl} alt={img.filename} loading="lazy" />
                    : <div className="thumb-fallback">{img.filename}</div>}
                  <span className="badge-seq">#{img.seq}</span>
                </div>
                <div className="image-meta">
                  <div className="image-name" title={img.filename}>{img.filename}</div>
                  <div className="image-buttons">
                    <button type="button" className="btn btn-sm" onClick={() => moveImage(index, -1)} disabled={index === 0}>↑</button>
                    <button type="button" className="btn btn-sm" onClick={() => moveImage(index, 1)} disabled={index === images.length - 1}>↓</button>
                    {canEdit && (
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(img)}>
                        {zh ? '刪除' : 'Delete'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {images.length > 0 && images.length !== expectedCount && (
          <p className="hint warn-hint">
            {zh
              ? `目前 ${images.length} 張（原定 ${expectedCount} 張）— 數量與預計不同沒問題，Apply 後按現有次序重新編號。`
              : `${images.length} uploaded (expected ${expectedCount}) — that's fine; Apply renumbers the current order.`}
          </p>
        )}
      </div>
      <style jsx>{pageStyles}</style>
    </div>
  )
}

const pageStyles = `
  .ministry-page { max-width: 1200px; margin: 0 auto; }
  .page-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px; }
  .page-top h2 { margin: 0 0 4px; }
  .sub { margin: 0; color: var(--text-secondary); font-size: 0.9rem; }
  .card {
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
  }
  .form-row { display: flex; gap: 20px; flex-wrap: wrap; align-items: flex-start; }
  .form-group { display: flex; flex-direction: column; gap: 8px; min-width: 140px; }
  .form-group label { font-size: 0.875rem; color: var(--text-secondary); }
  .form-group select,
  .form-group input[type="text"] {
    padding: 10px 12px;
    border-radius: 6px;
    border: 2px solid var(--input-border, var(--border-color));
    background: var(--bg-input, var(--bg-tertiary));
    color: var(--text-primary);
    width: 160px;
  }
  .form-group-date { flex: 1; min-width: 260px; }
  .quick-dates { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
  .quick-label { font-size: 0.8rem; color: var(--text-secondary); }
  .btn-quick-date {
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid var(--border-color);
    background: var(--bg-tertiary);
    color: var(--text-primary);
    cursor: pointer;
    font-size: 0.8rem;
  }
  .btn-quick-date:hover { border-color: var(--accent-primary); }
  .count-stepper { display: flex; align-items: center; gap: 12px; }
  .count-stepper button {
    width: 32px; height: 32px; border-radius: 6px;
    border: 1px solid var(--border-color);
    background: var(--bg-tertiary); color: var(--text-primary);
    font-size: 1rem; cursor: pointer;
  }
  .count-stepper span { min-width: 24px; text-align: center; font-weight: 600; }
  .btn-upload { white-space: nowrap; }
  .hint { font-size: 0.85rem; color: var(--text-secondary); margin: 10px 0 0; }
  .warn-hint { color: var(--accent-primary, #f59e0b); }
  .images-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
  .images-header h3 { margin: 0; font-size: 1rem; }
  .images-actions { display: flex; gap: 10px; align-items: center; }
  .dirty-hint { font-size: 0.85rem; color: var(--accent-primary); }
  .image-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 14px; }
  .image-card { border: 1px solid var(--border-color); border-radius: 8px; overflow: hidden; background: var(--bg-tertiary); }
  .thumb-wrap { position: relative; aspect-ratio: 16/9; background: var(--bg-secondary); }
  .thumb-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .thumb-fallback { display: flex; align-items: center; justify-content: center; height: 100%; font-size: 0.8rem; color: var(--text-secondary); padding: 8px; text-align: center; }
  .badge-seq {
    position: absolute; top: 6px; left: 6px;
    background: rgba(0,0,0,0.65); color: #fff;
    border-radius: 4px; padding: 2px 8px; font-size: 0.8rem; font-weight: 600;
  }
  .image-meta { padding: 10px; display: flex; flex-direction: column; gap: 8px; }
  .image-name { font-size: 0.8rem; color: var(--text-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .image-buttons { display: flex; gap: 6px; }
  .btn-sm { padding: 4px 10px; font-size: 0.8rem; }
  .btn-danger { color: #f87171; border-color: rgba(248,113,113,0.4); }
`